import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

type MultiSelectionStoreType = {
  selectedIds: string[];
  setSelection: (ids: string[]) => void;
  add: (id: string) => void;
  remove: (id: string) => void;
  toggle: (id: string) => void;
  clear: () => void;
  isSelected: (id: string) => boolean;
};

const useMultiSelection = create<MultiSelectionStoreType>((set, get) => ({
  selectedIds: [],

  setSelection: (ids: string[]) => {
    set({ selectedIds: Array.from(new Set(ids)) });
  },

  add: (id: string) => {
    set(({ selectedIds }) =>
      selectedIds.includes(id)
        ? { selectedIds }
        : { selectedIds: [...selectedIds, id] },
    );
  },

  remove: (id: string) => {
    set(({ selectedIds }) => ({
      selectedIds: selectedIds.filter((selectedId) => selectedId !== id),
    }));
  },

  toggle: (id: string) => {
    set(({ selectedIds }) => ({
      selectedIds: selectedIds.includes(id)
        ? selectedIds.filter((selectedId) => selectedId !== id)
        : [...selectedIds, id],
    }));
  },

  clear: () => {
    if (get().selectedIds.length === 0) return;
    set({ selectedIds: [] });
  },

  isSelected: (id: string) => get().selectedIds.includes(id),
}));

export const useIsNodeMultiSelected = (id: string) =>
  useMultiSelection((state) => state.selectedIds.includes(id));

export const useMultiSelectionCount = () =>
  useMultiSelection((state) => state.selectedIds.length);

export const useMultiSelectionActions = () =>
  useMultiSelection(
    useShallow((state) => ({
      setSelection: state.setSelection,
      add: state.add,
      remove: state.remove,
      toggle: state.toggle,
      clear: state.clear,
      isSelected: state.isSelected,
    })),
  );

export const {
  getState: getMultiSelectionState,
  setState: setMultiSelectionState,
} = useMultiSelection;

export default useMultiSelection;
